/**
 * A service's parameters as Add extra asks for them (services spec 10): which
 * the ticked activities ask for, what each typed value is on the wire, and the
 * dictionary's rules said as Classic's dialog says them. One home for Classic's
 * `AddExtraDialog` and One's parameters widget (the catalogue's
 * `AddExtraServiceForm` parameters; the dictionary checks the same on submit).
 */

/** One parameter of a service: an option to choose, or a whole number within bounds. */
export interface ServiceParameterForm {
  key: string;
  label: string;
  value_type: "option" | "integer";
  options: readonly { code: string; label: string }[];
  min_value: number | null;
  max_value: number | null;
  required: boolean;
  /** Asked only when one of these activities is ticked; empty means always. */
  when_activity: readonly string[];
}

/** The parameters to ask for: those with no activity condition, or whose activity is ticked. */
export function applicableParameters<P extends ServiceParameterForm>(
  parameters: readonly P[],
  activities: readonly string[],
): P[] {
  return parameters.filter(
    (p) =>
      p.when_activity.length === 0 ||
      p.when_activity.some((a) => activities.includes(a)),
  );
}

/** A typed whole number ("12", "12.0"), or null when the text isn't one. */
function wholeNumberOf(text: string): number | null {
  const n = Number(text);
  return Number.isInteger(n) ? n : null;
}

/**
 * The parameters as the body sends them: option codes, and whole numbers as
 * numbers; only those asked for and given. A number box holding something
 * that isn't a whole number sends nothing (its problem is said beside it).
 */
export function parametersBody(
  parameters: readonly ServiceParameterForm[],
  values: Readonly<Record<string, string>>,
): Record<string, string | number> {
  const body: Record<string, string | number> = {};
  for (const p of parameters) {
    const value = (values[p.key] ?? "").trim();
    if (!value) continue;
    if (p.value_type === "option") body[p.key] = value;
    else {
      const n = wholeNumberOf(value);
      if (n !== null) body[p.key] = n;
    }
  }
  return body;
}

/** "Enter a whole number from 1 to 50." */
export function wholeNumberMessage(p: ServiceParameterForm): string {
  return (
    `Enter a whole number from ${(p.min_value ?? 0).toLocaleString("en-GB")}` +
    (p.max_value !== null ? ` to ${p.max_value.toLocaleString("en-GB")}.` : ".")
  );
}

/** One parameter's problem as typed, in Classic's words; undefined when there is none. */
export function parameterProblem(
  p: ServiceParameterForm,
  raw: string | undefined,
): string | undefined {
  const value = (raw ?? "").trim();
  if (!value)
    return p.required ? `Choose the ${p.label.toLowerCase()}.` : undefined;
  if (p.value_type !== "integer") return undefined;
  const n = wholeNumberOf(value);
  return n === null ||
    (p.min_value !== null && n < p.min_value) ||
    (p.max_value !== null && n > p.max_value)
    ? wholeNumberMessage(p)
    : undefined;
}

/** Every asked-for parameter's problem as typed, by its body path (`parameters.crew_size`). */
export function parameterProblems(
  parameters: readonly ServiceParameterForm[],
  values: Readonly<Record<string, string>>,
): Record<string, string | undefined> {
  return Object.fromEntries(
    parameters.map((p) => [
      `parameters.${p.key}`,
      parameterProblem(p, values[p.key]),
    ]),
  );
}

/** A body's parameters as the boxes show them: every value as text. */
export function parameterTexts(body: unknown): Record<string, string> {
  if (!body || typeof body !== "object" || Array.isArray(body)) return {};
  return Object.fromEntries(
    Object.entries(body as Record<string, unknown>).map(([key, value]) => [
      key,
      String(value ?? ""),
    ]),
  );
}
